import { memo, useEffect, useState } from 'react'
import { Box, Grid, IconButton, MenuItem, Stack, TextField, Tooltip } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import Section from 'components/common/Section/Section'
import CustomText from 'components/common/CustomText/CustomText'
import CustomButton from 'components/buttons/CustomButton/CustomButton'
import CustomLabel from 'components/common/CustomLabel/CustomLabel'
import CustomDialog from 'components/dialogs/CustomDialog'
import PencilIcon from 'icons/PencilIcon'
import SaveIcon from 'icons/SaveIcon'
import XIcon from 'icons/XIcon'
import { colors } from 'styles/colors'
import { useProductContext } from 'context/ProductContext'
import { useUpdateProductRankedIngredients } from 'hooks/useProduct'
import { snackbarService } from 'services/snackbarService'

interface RankedRow {
  en: string;
  ar: string;
  // '' is the row nobody has answered for, which is not the same as either side.
  worksOn: '' | 'person' | 'mixture';
}

const WORKS_ON = [
  { value: '', label: 'Not set' },
  { value: 'person', label: 'Person' },
  { value: 'mixture', label: 'Mixture' },
];

const emptyRow = (): RankedRow => ({ en: '', ar: '', worksOn: '' });

const fromProduct = (ranked: any): RankedRow[] => {
  if (!Array.isArray(ranked)) return [];

  return ranked.map((item: any) => ({
    en: item?.en ?? '',
    ar: item?.ar ?? '',
    worksOn: item?.worksOn ?? '',
  }));
};

const toProduct = (rows: RankedRow[]) =>
  rows
    .filter((row) => row.en.trim() || row.ar.trim())
    .map((row) => ({
      en: row.en.trim(),
      ar: row.ar.trim(),
      ...(row.worksOn ? { worksOn: row.worksOn } : {}),
    }));

// The panel in the order the pack prints it, which is the order that says how
// much of each is in the product - so the rows are moved rather than sorted, and
// the number beside each one is the answer this section exists to hold.
function RankedIngredientsDetails() {
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const { product, refetch: refetchProduct } = useProductContext();
  const [rows, setRows] = useState<RankedRow[]>([]);
  const [removingRow, setRemovingRow] = useState<number | null>(null);
  // A long list that is only read when something about it is in question, so it
  // stays folded away until asked for - the same as the label text above it.
  const [isVisible, setIsVisible] = useState<boolean>(false);

  useEffect(() => {
    setRows(fromProduct(product.rankedIngredients));
  }, [product.rankedIngredients]);

  const { mutate: updateMutation, isLoading } = useUpdateProductRankedIngredients(
    () => {
      setIsEditing(false);
      snackbarService.showSnackbar('Ordered ingredients have been updated successfully', 'success');
      refetchProduct();
    },
    (err: any) => {
      snackbarService.showSnackbar('Could not update the ordered ingredients', 'error');
    },
  )

  const setField = (index: number, field: keyof RankedRow, value: string) =>
    setRows((previous) =>
      previous.map((row, i) => (i === index ? { ...row, [field]: value } : row)),
    );

  const move = (index: number, by: number) =>
    setRows((previous) => {
      const next = [...previous];
      const target = index + by;
      if (target < 0 || target >= next.length) return previous;

      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });

  const handleSave = () => {
    updateMutation({ id: product.id, rankedIngredients: toProduct(rows) as any });
  }

  const handleCancel = () => {
    setRows(fromProduct(product.rankedIngredients));
    setIsEditing(false);
  }

  return (
    <Box mt={3}>
      <Section
        title="Ordered Ingredients"
        subtitle={rows.length ? `${rows.length} ordered` : undefined}
        buttons={[
          ...(isEditing
            ? [
              {
                icon: <SaveIcon size={20} color={colors.success} />,
                title: 'Save',
                onClick: () => handleSave(),
                loading: isLoading,
              },
              {
                icon: <XIcon size={15} color={colors.error} />,
                title: 'Cancel',
                onClick: () => handleCancel(),
                disabled: isLoading,
              },
            ]
            : [
              {
                icon: <PencilIcon size={20} color={colors.primary} />,
                title: 'Edit',
                onClick: () => {
                  setIsVisible(true);
                  setIsEditing(true);
                },
                disabled: isLoading,
              }
            ]),
        ]}
        collapsed={!isVisible}
        onToggle={isEditing ? undefined : () => setIsVisible((prev) => !prev)}
        content={isVisible &&
          <Grid size={{ xs: 12 }}>
            {rows.length === 0 && !isEditing && (
              <CustomText
                text={'The panel has not been put in order yet.'}
                fontSize={14}
                color={colors.gray}
              />
            )}

            <Stack gap={1.5}>
              {rows.map((row, index) => (
                <Stack key={index} direction="row" alignItems="flex-start" gap={1}>
                  <Box sx={{ pt: 3.5, minWidth: 28 }}>
                    <CustomText text={`${index + 1}.`} fontSize={14} color={colors.gray} />
                  </Box>

                  <Grid container spacing={1.5} flex={1}>
                    <Grid size={{ xs: 12, md: 5 }}>
                      <CustomLabel label={'English Name'} />
                      <TextField
                        size="small"
                        fullWidth
                        value={row.en}
                        disabled={!isEditing}
                        onChange={(event) => setField(index, 'en', event.target.value)}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 4 }}>
                      <CustomLabel label={'Arabic Name'} />
                      <TextField
                        size="small"
                        fullWidth
                        value={row.ar}
                        disabled={!isEditing}
                        onChange={(event) => setField(index, 'ar', event.target.value)}
                        sx={{ '& input': { direction: 'rtl' } }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, md: 3 }}>
                      <CustomLabel label={'Works On'} />
                      <TextField
                        select
                        size="small"
                        fullWidth
                        value={row.worksOn}
                        disabled={!isEditing}
                        onChange={(event) => setField(index, 'worksOn', event.target.value)}
                      >
                        {WORKS_ON.map((option) => (
                          <MenuItem key={option.value || 'not-set'} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>

                  {isEditing && (
                    <Stack direction="row" sx={{ pt: 2.75 }}>
                      <Tooltip title="Move up">
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === 0}
                            onClick={() => move(index, -1)}
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Move down">
                        <span>
                          <IconButton
                            size="small"
                            disabled={index === rows.length - 1}
                            onClick={() => move(index, 1)}
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>

                      <Tooltip title="Remove this ingredient">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setRemovingRow(index)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  )}
                </Stack>
              ))}

              {isEditing && (
                <CustomButton
                  text={'Add Ingredient'}
                  variant={'outlined'}
                  startIcon={<AddIcon />}
                  height={40}
                  fullWidth
                  onClick={() => setRows((previous) => [...previous, emptyRow()])}
                />
              )}
            </Stack>
          </Grid>
        }
      />

      <CustomDialog
        open={removingRow !== null}
        title={'Remove Ingredient'}
        message={'This removes the row from the ordered panel. It is only stored once you save.'}
        buttons={[
          <CustomButton
            key={'cancel'}
            variant={'outlined'}
            text={'Cancel'}
            textColor={'error'}
            height={35}
            onClick={() => setRemovingRow(null)}
          />,
          <CustomButton
            key={'remove'}
            variant={'filled'}
            color={'error'}
            text={'Remove'}
            height={35}
            onClick={() => {
              setRows((previous) => previous.filter((_row, i) => i !== removingRow));
              setRemovingRow(null);
            }}
          />,
        ]}
      />
    </Box>
  )
}

export default memo(RankedIngredientsDetails)
